import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import nlGlobal from './nl/global.json';
import enGlobal from './en/global.json';
import deGlobal from './de/global.json';
import trGlobal from './tr/global.json';

/**
 * GeoNexora speaks Dutch, English, German and Turkish. The language is the one chosen before (localStorage
 * 'language'), else the browser's first one we speak, else English. Every key exists in all four files
 * (npm run check:i18n); English is the fallback for anything a release might still miss.
 */
const SUPPORTED = ['nl', 'en', 'de', 'tr'];
const STORAGE_KEY = 'language';

function initialLanguage(): string {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch {
    /* storage unavailable */
  }
  const browser = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
  for (const l of browser) {
    const short = (l || '').substring(0, 2).toLowerCase();
    if (SUPPORTED.includes(short)) return short;
  }
  return 'en';
}

function markDocument(lng: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
}

i18n
  .use(initReactI18next)
  .init({
    lng: initialLanguage(),
    supportedLngs: SUPPORTED,
    nonExplicitSupportedLngs: true,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    resources: {
      nl: { translation: nlGlobal },
      en: { translation: enGlobal },
      de: { translation: deGlobal },
      tr: { translation: trGlobal },
    },
  });

markDocument(i18n.language);
i18n.on('languageChanged', markDocument);

/** Switches the language and remembers it. */
export function setLanguage(lng: string): void {
  const l = SUPPORTED.includes(lng) ? lng : 'en';
  try {
    localStorage.setItem(STORAGE_KEY, l);
  } catch {
    /* storage unavailable */
  }
  void i18n.changeLanguage(l);
}

export default i18n;
